import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import {
  Bell, Mail, MessageSquare, CheckCircle, XCircle, Clock, RefreshCw,
  FlaskConical, X, AlertTriangle, ChevronDown, ChevronUp, Plus, Edit,
  Trash2, Info, History, MessageCircle, CheckCircle2, AlertCircle, Receipt
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// ─── Constantes Templates (automatismes système uniquement) ──────────────────
const CATEGORIES = [
  { value: 'sms_automatique', label: 'SMS — Automatique',     color: 'bg-orange-100 text-orange-800' },
  { value: 'sms_employe',     label: 'SMS — Employés',        color: 'bg-pink-100 text-pink-800' },
  { value: 'sms_proprio',     label: 'SMS — Propriétaire',    color: 'bg-red-100 text-red-800' },
  { value: 'notification_signature',             label: 'Courriel auto — Soumission signée',          color: 'bg-blue-100 text-blue-800' },
  { value: 'notification_commande_peinture',     label: 'Courriel auto — Commande peinture',          color: 'bg-blue-100 text-blue-800' },
  { value: 'notification_travaux_termines',      label: 'Courriel auto — Travaux terminés',           color: 'bg-blue-100 text-blue-800' },
  { value: 'notification_codes_couleur',         label: 'Courriel auto — Codes couleur reçus',        color: 'bg-blue-100 text-blue-800' },
  { value: 'notification_avertissement_employe', label: 'Courriel auto — Avertissement employé',      color: 'bg-blue-100 text-blue-800' },
  { value: 'notification_vehicule_incident',     label: 'Courriel auto — Bris véhicule',              color: 'bg-blue-100 text-blue-800' },
  { value: 'resume_hebdomadaire',                label: 'Courriel auto — Résumé hebdomadaire (Lundi)', color: 'bg-blue-100 text-blue-800' },
  { value: 'notification_zero_heure_assurance',  label: 'Courriel auto — Alerte 0h assurance collective', color: 'bg-red-100 text-red-800' },
];

const SMS_TRIGGERS = {
  avertissement: 'Déclenché quand un avertissement est ajouté au dossier employé',
  commande_materiaux: 'Déclenché quand un chef clique "Commander matériaux"',
  contrat_signe_rep: 'Déclenché quand un contrat est signé (envoyé à la représentante)',
  contrat_signe_cp: 'Déclenché quand un contrat est signé (envoyé à la chargée de projet)',
  lavage_en_route: "Déclenché quand l'équipe de lavage démarre (\"Départ bureau\")",
  bonus: 'Déclenché une fois par semaine après recalcul du bonus',
  travaux_termines: 'Déclenché quand un chef clique "Travaux terminés"',
  rapport_lmds: 'Déclenché quand un rapport hebdomadaire LMDS est soumis',
  depot_recu: "Déclenché quand un dépôt est marqué comme payé",
  paiement_final: 'Déclenché quand le paiement final est marqué comme reçu',
  avis_google: 'Déclenché manuellement via le bouton "Demander Avis Google"',
  rappel_non_payee: 'Déclenché automatiquement après 15+ jours sans paiement LMDS',
  rappel_non_payee_hebdo: 'Rappel hebdomadaire — installations LMDS non payées depuis 15+ jours',
  absence_alerte: "Déclenché 31 minutes après l'heure de début prévue si un employé n'est pas pointé",
  rapport_primes_proprio: 'Envoyé chaque mercredi matin à 8h — Résumé des primes horaires',
};

const SMS_CATS = ['sms_automatique', 'sms_employe', 'sms_proprio'];

const VARIABLES_PAR_CATEGORIE = {
  rendez_vous: ['client_nom', 'date_rdv', 'heure_rdv', 'representant', 'telephone_representant', 'adresse_travaux', 'lien_confirmation', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'lien_soumission', 'avis_google', 'logo_email'],
  soumission: ['client_nom', 'numero_soumission', 'montant', 'montant_depot', 'lien_soumission', 'avis_google', 'representant', 'telephone_representant', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'logo_email'],
  presentation: ['client_nom', 'nom_dossier', 'adresse_travaux', 'dates_chantier', 'chef_equipe', 'telephone_chef_equipe', 'charge_projet', 'telephone_charge_projet', 'representant', 'telephone_representant', 'tableau_produits', 'lien_codes_couleur', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'avis_google', 'logo_email'],
  description_travaux: ['client_nom', 'adresse_travaux', 'type_travaux', 'representant', 'telephone_representant', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'lien_soumission', 'avis_google', 'logo_email'],
  notes_soumission: ['client_nom', 'projet', 'date', 'representant', 'telephone_representant', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'lien_soumission', 'avis_google', 'logo_email'],
  termes_conditions: ['client_nom', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'representant', 'telephone_representant', 'lien_soumission', 'avis_google', 'logo_email'],
  document_garantie: ['client_nom', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'duree_garantie', 'representant', 'telephone_representant', 'lien_soumission', 'avis_google', 'logo_email'],
  document_location: ['client_nom', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'equipement_nom', 'duree_location', 'representant', 'telephone_representant', 'lien_soumission', 'avis_google', 'logo_email'],
  document_contractuel: ['client_nom', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'date', 'numero_contrat', 'representant', 'telephone_representant', 'lien_soumission', 'avis_google', 'logo_email'],
  facture: ['client_nom', 'numero_facture', 'montant_total', 'montant_du', 'date_echeance', 'representant', 'telephone_representant', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'lien_soumission', 'avis_google', 'logo_email'],
  suivi: ['client_nom', 'representant', 'telephone_representant', 'nom_projet', 'jours_depuis_envoi', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'lien_soumission', 'avis_google', 'logo_email'],
  sms_automatique: ['client_nom', 'nom_projet', 'montant', 'si_number', 'date_installation', 'semaine', 'lien_application'],
  sms_employe: ['prenom', 'nom_projet', 'bonus_actuel', 'chef_prenom', 'quantite', 'semaine', 'lien_application'],
  sms_proprio: ['periode_debut', 'periode_fin', 'liste_primes', 'total_primes', 'nb_employes'],
  autre: ['client_nom', 'nom_destinataire', 'date', 'nom_expediteur', 'representant', 'telephone_representant', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'lien_soumission', 'avis_google', 'logo_email'],
  notification_signature: ['client_nom', 'nom_projet', 'numero_dossier', 'montant', 'adresse'],
  notification_commande_peinture: ['nom_projet', 'numero_dossier', 'client_nom', 'chef_equipe', 'produit_nom', 'fini', 'quantite', 'unite', 'emplacement'],
  notification_travaux_termines: ['client_nom', 'nom_projet', 'numero_dossier', 'type_travaux', 'chef_equipe'],
  notification_codes_couleur: ['client_nom', 'numero_dossier', 'liste_codes'],
  notification_avertissement_employe: ['prenom_employe', 'type_avertissement', 'date_incident', 'description', 'notes', 'total_avertissements', 'seuil_max', 'saison', 'message_seuil'],
  notification_vehicule_incident: ['vehicule_nom', 'plaque', 'numero_serie', 'declare_par', 'date', 'description'],
  resume_hebdomadaire: ['charge_projet', 'semaine', 'date_debut', 'date_fin', 'liste_projets', 'liste_conges'],
  notification_zero_heure_assurance: ['employee_nom', 'periode_debut', 'periode_fin', 'assurance_h', 'deduction_manquante'],
};

// Catégories gérées dans la page "Messages Templates" (soumission + actions plateforme manuelles)
const SOUMISSION_CATS = [
  'notes_soumission', 'description_travaux', 'termes_conditions',
  'rendez_vous', 'soumission', 'presentation', 'facture', 'suivi',
  'document_garantie', 'document_location', 'document_contractuel', 'autre',
];

// ─── Constantes Registre ─────────────────────────────────────────────────────
const STATUS_CONFIG = {
  envoye:  { label: 'Envoyé',        icon: CheckCircle, cls: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  erreur:  { label: 'Échec',         icon: XCircle,     cls: 'text-red-600 bg-red-50 border-red-200' },
  pending: { label: 'En cours',      icon: Clock,       cls: 'text-amber-600 bg-amber-50 border-amber-200' },
  jamais:  { label: 'Jamais envoyé', icon: Clock,       cls: 'text-slate-400 bg-slate-50 border-slate-200' },
  test:    { label: 'Test',          icon: FlaskConical, cls: 'text-blue-600 bg-blue-50 border-blue-200' },
};

// ─── Config badges déclencheurs ──────────────────────────────────────────────
const TRIGGER_TYPE_CONFIG = {
  auto:         { label: 'Automatique',   cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  bouton:       { label: 'Bouton',        cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  planifie:     { label: 'Planifié',      cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  conditionnel: { label: 'Conditionnel',  cls: 'bg-amber-100 text-amber-700 border-amber-200' },
};

// ─── Config canal ─────────────────────────────────────────────────────────────
const CANAL_ICON = {
  email:     { icon: Mail,         cls: 'bg-blue-100 text-blue-600' },
  sms:       { icon: MessageSquare, cls: 'bg-violet-100 text-violet-600' },
  'sms+email': { icon: Bell,       cls: 'bg-indigo-100 text-indigo-600' },
};

// ─── Sous-composants ──────────────────────────────────────────────────────────
function StatusBadge({ status, small }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.jamais;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${cfg.cls} ${small ? 'py-0' : ''}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function EventCard({ event, onTest, onViewLogs, onEdit, testing }) {
  const [expanded, setExpanded] = useState(false);
  const isError = event.dernier_statut === 'erreur';
  const notImplemented = event.implemente === false;
  const triggerCfg = TRIGGER_TYPE_CONFIG[event.trigger_type] || TRIGGER_TYPE_CONFIG.auto;
  const canalCfg = CANAL_ICON[event.canal] || CANAL_ICON.email;
  const CanalIcon = canalCfg.icon;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${
      notImplemented ? 'border-slate-200 bg-slate-50/50 opacity-75' :
      isError ? 'border-red-300 bg-red-50/30' :
      'border-slate-200 bg-white hover:border-slate-300'
    }`}>
      <div className="flex items-start gap-4 p-4">
        <div className={`mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${canalCfg.cls}`}>
          <CanalIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-slate-800 text-sm">{event.label}</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${triggerCfg.cls}`}>
                  {triggerCfg.label}
                </span>
                {notImplemented && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 border border-slate-300 text-xs font-medium text-slate-500">
                    À implémenter
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{event.description}</p>
            </div>
            <StatusBadge status={event.dernier_statut} />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Bell className="w-3 h-3" />
              <span className="font-medium text-slate-600">Déclencheur :</span> {event.trigger}
            </span>
            <span className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              <span className="font-medium text-slate-600">Vers :</span> {event.destinataires?.join(', ')}
            </span>
            {event.dernier_envoi && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Dernier : {new Date(event.dernier_envoi).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' })}
              </span>
            )}
          </div>
          {event.stats_30j?.total > 0 && (
            <div className="mt-2 flex gap-3 text-xs">
              <span className="text-emerald-700 font-medium">{event.stats_30j.succes} succès</span>
              {event.stats_30j.erreurs > 0 && <span className="text-red-600 font-medium">{event.stats_30j.erreurs} échec{event.stats_30j.erreurs > 1 ? 's' : ''}</span>}
              <span className="text-slate-400">/ 30 jours</span>
            </div>
          )}
          {isError && event.dernier_erreur && (
            <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-xs text-red-700 font-mono">
              {event.dernier_erreur}
            </div>
          )}
          {expanded && (
            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 space-y-1">
              <p><span className="font-medium text-slate-600">ID :</span> {event.id}</p>
              {event.template_code && <p><span className="font-medium text-slate-600">Template :</span> <code className="bg-slate-100 px-1 rounded">{event.template_code}</code></p>}
              <p><span className="font-medium text-slate-600">Canal :</span> {event.canal}</p>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button
            size="sm" variant="outline"
            onClick={() => onTest(event)}
            disabled={testing || notImplemented}
            title={notImplemented ? 'Automatisation non encore implémentée' : 'Envoyer un test'}
            className="text-xs h-7 gap-1 whitespace-nowrap"
            data-testid={`btn-test-${event.id}`}
          >
            <FlaskConical className="w-3 h-3" /> Tester
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={() => onEdit(event)}
            className="text-xs h-7 gap-1 whitespace-nowrap text-blue-600 border-blue-200 hover:bg-blue-50"
            data-testid={`btn-edit-${event.id}`}
          >
            <Edit className="w-3 h-3" /> Modifier
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => onViewLogs(event.id)}
            className="text-xs h-7 gap-1 text-slate-500 whitespace-nowrap"
          >
            Historique
          </Button>
          <button
            onClick={() => setExpanded(e => !e)}
            className="text-xs text-slate-400 hover:text-slate-600 flex items-center justify-center gap-0.5"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
      </div>
    </div>
  );
}


function FailureAlert({ failures, onDismiss, onDismissAll }) {
  if (!failures.length) return null;
  return (
    <div className="mb-6 bg-red-50 border border-red-300 rounded-xl p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-red-600" />
          </div>
          <div>
            <p className="font-semibold text-red-800 text-sm">
              {failures.length} échec{failures.length > 1 ? 's' : ''} non lu{failures.length > 1 ? 's' : ''}
            </p>
            <div className="mt-1 space-y-1">
              {failures.slice(0, 3).map(f => (
                <div key={f.id} className="flex items-center gap-2 text-xs text-red-700">
                  <span className="font-medium">{f.event_id || f.template_code}</span>
                  <span className="text-red-500">→</span>
                  <span>{f.to}</span>
                  <span className="text-red-400">— {f.error?.slice(0, 60)}</span>
                  <button onClick={() => onDismiss(f.id)} className="ml-1 hover:text-red-900">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {failures.length > 3 && <p className="text-xs text-red-500">... et {failures.length - 3} autre(s)</p>}
            </div>
          </div>
        </div>
        <Button
          size="sm" variant="outline"
          onClick={onDismissAll}
          className="text-xs border-red-300 text-red-700 hover:bg-red-100 shrink-0"
          data-testid="btn-dismiss-all-failures"
        >
          <X className="w-3 h-3 mr-1" /> Tout marquer comme lu
        </Button>
      </div>
    </div>
  );
}

function LogsPanel({ eventId, headers, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/notifications/logs?event_id=${eventId}&limit=50`, { headers })
      .then(r => r.data)
      .then(d => setLogs(d.logs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventId, headers]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-slate-800">Historique — <span className="text-slate-500 font-normal">{eventId}</span></h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto p-4 space-y-2">
          {loading && <p className="text-slate-400 text-sm text-center py-8">Chargement...</p>}
          {!loading && logs.length === 0 && <p className="text-slate-400 text-sm text-center py-8">Aucun envoi enregistré pour cet événement.</p>}
          {logs.map((log, i) => (
            <div key={log.id || i} className={`flex items-start gap-3 p-3 rounded-lg border text-sm ${log.status === 'erreur' ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
              <StatusBadge status={log.status} small />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-700 truncate">{log.to}</p>
                {log.subject && <p className="text-xs text-slate-500 truncate">{log.subject}</p>}
                {log.body && !log.subject && <p className="text-xs text-slate-500 truncate">{log.body?.slice(0, 80)}</p>}
                {log.error && <p className="text-xs text-red-600 mt-1 font-mono">{log.error}</p>}
              </div>
              <span className="text-xs text-slate-400 shrink-0">
                {new Date(log.sent_at).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' })}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function CentreNotifications() {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // ── Onglet actif ──
  const [mainTab, setMainTab] = useState('actions');

  // ── État Actions en attente ──
  const [actionBadges, setActionBadges] = useState(null);
  const [loadingActions, setLoadingActions] = useState(true);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loadingRecentLogs, setLoadingRecentLogs] = useState(false);
  const [recentLogsLimit, setRecentLogsLimit] = useState(15);

  // ── État Registre ──
  const [events, setEvents] = useState([]);
  const [failures, setFailures] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [registreFilter, setRegistreFilter] = useState('all');
  const [logsEventId, setLogsEventId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  // Dialog de test
  const [testDialog, setTestDialog] = useState(null); // { event } | null
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');

  // ── État Templates ──
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [filtreCategorie, setFiltreCategorie] = useState('tous');
  const [customCategories, setCustomCategories] = useState([]);
  const [showAddCategorie, setShowAddCategorie] = useState(false);
  const [newCategorieName, setNewCategorieName] = useState('');
  const [formData, setFormData] = useState({
    nom: '', categorie: 'rendez_vous', sujet: '', contenu: '', variables_disponibles: [],
  });

  // ── État Historique ──
  const [historique, setHistorique] = useState([]);
  const [loadingHistorique, setLoadingHistorique] = useState(false);
  const [filtreCanal, setFiltreCanal] = useState('tous');
  const [expandedRows, setExpandedRows] = useState({});
  const [histPage, setHistPage] = useState(1);
  const [messageDetailLog, setMessageDetailLog] = useState(null);
  const HIST_PER_PAGE = 25;

  const allCategories = [...CATEGORIES, ...customCategories];
  const isSmsCategory = SMS_CATS.includes(formData.categorie);

  // ── Fetch actions en attente ──
  const fetchActions = useCallback(async () => {
    setLoadingActions(true);
    try {
      const [badgesRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications/badges`, { headers }).then(r => r.data),
        axios.get(`${API_URL}/api/notifications/logs?limit=100`, { headers }).then(r => r.data),
      ]);
      setActionBadges(badgesRes);
      setRecentLogs(logsRes.logs || []);
    } catch {
      // silencieux
    } finally {
      setLoadingActions(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchActions(); }, [fetchActions]);

  // ── Fetch registre ──
  const fetchRegistre = useCallback(async () => {
    setRefreshing(true);
    try {
      const [eventsRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications/events`, { headers }).then(r => r.data),
        axios.get(`${API_URL}/api/notifications/logs?status=erreur&limit=20`, { headers }).then(r => r.data),
      ]);
      setEvents(eventsRes.events || []);
      setFailures((logsRes.logs || []).filter(l => !l.lu));
    } catch {
      toast.error('Erreur lors du chargement du registre');
    } finally {
      setLoadingEvents(false);
      setRefreshing(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRegistre(); }, [fetchRegistre]);

  // ── Fetch templates (exclure les catégories soumission gérées dans Messages Templates) ──
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await axios.get(`${API_URL}/api/messages-templates`);
      if (res.status !== 200) throw new Error();
      const data = res.data;
      // Exclure les catégories soumission qui appartiennent à la page "Messages Templates"
      const tpls = (data.templates || []).filter(t => !SOUMISSION_CATS.includes(t.categorie));
      setTemplates(tpls);
      const knownValues = CATEGORIES.map(c => c.value);
      const customs = [...new Set(tpls.map(t => t.categorie).filter(c => !knownValues.includes(c)))];
      setCustomCategories(customs.map(c => ({
        value: c,
        label: c.charAt(0).toUpperCase() + c.slice(1).replace(/_/g, ' '),
        color: 'bg-slate-100 text-slate-800',
      })));
    } catch {
      toast.error('Erreur chargement templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    if (mainTab === 'templates') fetchTemplates();
  }, [mainTab, fetchTemplates]);

  // ── Fetch historique ──
  const fetchHistorique = useCallback(async () => {
    setLoadingHistorique(true);
    try {
      const params = filtreCanal !== 'tous' ? `?channel=${filtreCanal}` : `?limit=100`;
      const res = await axios.get(`${API_URL}/api/sms/historique-unifie${params}`);
      if (res.status !== 200) throw new Error();
      const data = res.data;
      setHistorique(data.logs || []);
    } catch {
      toast.error("Impossible de charger l'historique");
    } finally {
      setLoadingHistorique(false);
    }
  }, [filtreCanal]);

  useEffect(() => {
    if (mainTab === 'historique') fetchHistorique();
  }, [mainTab, fetchHistorique]);

  // ── Sync variables au changement de catégorie ──
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      variables_disponibles: VARIABLES_PAR_CATEGORIE[prev.categorie] || [],
    }));
  }, [formData.categorie]);

  // ── Actions registre ──
  const openTestDialog = (event) => {
    const user = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || '{}');
    setTestEmail(user.email || 'info@peintureresidentielleqc.com');
    setTestPhone('');
    setTestDialog(event);
  };

  const handleTest = async () => {
    if (!testDialog) return;
    setTestingId(testDialog.id);
    try {
      const res = await axios.post(`${API_URL}/api/notifications/test/${testDialog.id}`, { test_email: testEmail, test_phone: testPhone }, { headers });
      const data = res.data;
      if (data.sent_to?.length > 0) {
        toast.success(`Test envoyé → ${data.sent_to.join(' | ')}`);
      }
      if (data.errors?.length > 0) {
        data.errors.forEach(e => toast.error(e));
      }
      setTestDialog(null);
      fetchRegistre();
    } catch {
      toast.error('Erreur lors du test');
    } finally {
      setTestingId(null);
    }
  };

  // Ouvrir le template lié à une automatisation du Registre
  const handleEditFromRegistre = async (event) => {
    if (!event.template_code) {
      toast.error("Aucun template associé à cette automatisation");
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/messages-templates?search=${event.template_code}`, { headers });
      const data = res.data;
      const templates_list = data.templates || [];
      // Chercher le template par code exact ou par correspondance partielle
      const found = templates_list.find(t =>
        t.code_sms === event.template_code ||
        t.categorie === event.template_code ||
        t.nom?.toLowerCase().includes(event.template_code.replace(/_/g, ' '))
      );
      if (found) {
        setEditingTemplate(found);
        setFormData({ ...found, variables_disponibles: VARIABLES_PAR_CATEGORIE[found.categorie] || [] });
        setMainTab('templates'); // Basculer vers l'onglet Templates
        setShowModal(true);
        toast.info(`Template "${found.nom}" ouvert en édition`);
      } else {
        // Proposer de créer un nouveau template
        const catFromCode = CATEGORIES.find(c => c.value === event.template_code);
        setEditingTemplate(null);
        setFormData({
          nom: event.label,
          categorie: catFromCode ? event.template_code : CATEGORIES[0].value,
          sujet: event.label,
          contenu: '',
          variables_disponibles: VARIABLES_PAR_CATEGORIE[event.template_code] || [],
        });
        setMainTab('templates');
        setShowModal(true);
        toast.info(`Aucun template trouvé — créez-en un pour "${event.label}"`);
      }
    } catch {
      toast.error("Erreur lors de l'ouverture du template");
    }
  };

  const handleDismiss = async (logId) => {
    await axios.put(`${API_URL}/api/notifications/logs/${logId}/dismiss`, null, { headers });
    setFailures(f => f.filter(x => x.id !== logId));
  };

  const handleDismissAll = async () => {
    await axios.post(`${API_URL}/api/notifications/dismiss-all-failures`, null, { headers });
    setFailures([]);
    toast.success('Tous les échecs marqués comme lus');
  };

  // ── Actions templates ──
  const openCreateModal = () => {
    setEditingTemplate(null);
    setFormData({
      nom: '', categorie: 'rendez_vous', sujet: '',
      contenu: '', variables_disponibles: VARIABLES_PAR_CATEGORIE['rendez_vous'],
    });
    setShowModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!formData.nom || (!isSmsCategory && !formData.sujet) || !formData.contenu) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    try {
      const url = editingTemplate
        ? `${API_URL}/api/messages-templates/${editingTemplate.id}`
        : `${API_URL}/api/messages-templates`;
      const res = editingTemplate 
        ? await axios.put(url, formData, { headers })
        : await axios.post(url, formData, { headers });
      if (res.status !== 200) throw new Error();
      toast.success(editingTemplate ? 'Template modifié !' : 'Template créé !');
      setShowModal(false);
      fetchTemplates();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setFormData({ ...template, variables_disponibles: VARIABLES_PAR_CATEGORIE[template.categorie] || [] });
    setShowModal(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/messages-templates/${templateId}`, { headers });
      if (res.status !== 200) throw new Error();
      toast.success('Template supprimé !');
      fetchTemplates();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAddCategorie = () => {
    if (!newCategorieName.trim()) return;
    const value = newCategorieName.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (allCategories.find(c => c.value === value)) { toast.error('Cette catégorie existe déjà'); return; }
    setCustomCategories(prev => [...prev, { value, label: newCategorieName.trim(), color: 'bg-slate-100 text-slate-800' }]);
    setFormData(prev => ({ ...prev, categorie: value }));
    setNewCategorieName('');
    setShowAddCategorie(false);
    toast.success(`Catégorie "${newCategorieName.trim()}" ajoutée`);
  };

  const insererVariable = (variable) => {
    setFormData(prev => ({ ...prev, contenu: prev.contenu + `{${variable}}` }));
  };

  // ── Filtres registre ──
  const GROUPES = ['tous', 'Soumission / Ventes', 'Chantier / Production', 'RH', 'Planification', 'LMDS'];

  const filteredEvents = events.filter(e => {
    if (registreFilter === 'email') return e.canal === 'email';
    if (registreFilter === 'sms') return e.canal === 'sms' || e.canal === 'sms+email';
    if (registreFilter === 'errors') return e.dernier_statut === 'erreur';
    if (registreFilter === 'implemente') return e.implemente === false;
    if (registreFilter !== 'all' && !['email','sms','errors','implemente'].includes(registreFilter)) {
      return e.groupe === registreFilter;
    }
    return true;
  });
  const errorCount = events.filter(e => e.dernier_statut === 'erreur').length;

  // ── Filtres templates ──
  const templatesFiltres = templates.filter(t =>
    filtreCategorie === 'tous' ? true : t.categorie === filtreCategorie
  );

  // ── Onglets principaux ──
  const totalActions = actionBadges ? (actionBadges.conges + actionBadges.validation_heures + actionBadges.avertissements + actionBadges.todos + actionBadges.incidents) : 0;
  const mainTabs = [
    { id: 'actions',    label: 'Actions en attente', icon: AlertCircle, badge: totalActions > 0 ? totalActions : null, danger: true },
    { id: 'registre',   label: 'Registre',   icon: Bell,         badge: errorCount > 0 ? errorCount : null, danger: true },
    { id: 'templates',  label: 'Templates',  icon: MessageSquare, badge: templates.length || null },
    { id: 'historique', label: 'Historique', icon: History },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bell className="w-6 h-6 text-blue-600" />
            Centre de Notifications
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Contrôle central — registre, templates et historique de tous les courriels et SMS automatiques
          </p>
        </div>
        <div className="flex gap-2">
          {mainTab === 'actions' && (
            <Button variant="outline" size="sm" onClick={fetchActions} disabled={loadingActions} className="gap-2">
              <RefreshCw className={`w-4 h-4 ${loadingActions ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          )}
          {mainTab === 'templates' && (
            <Button onClick={openCreateModal} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="btn-new-template">
              <Plus className="w-4 h-4" /> Nouveau Template
            </Button>
          )}
          {mainTab === 'registre' && (
            <Button variant="outline" size="sm" onClick={fetchRegistre} disabled={refreshing} className="gap-2">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          )}
          {mainTab === 'historique' && (
            <Button variant="outline" size="sm" onClick={fetchHistorique} disabled={loadingHistorique} className="gap-2">
              <RefreshCw className={`w-4 h-4 ${loadingHistorique ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          )}
        </div>
      </div>

      {/* Alerte échecs */}
      <FailureAlert failures={failures} onDismiss={handleDismiss} onDismissAll={handleDismissAll} />

      {/* Onglets principaux */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {mainTabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setMainTab(tab.id)}
              data-testid={`main-tab-${tab.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                mainTab === tab.id ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
              {tab.badge != null && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${tab.danger && errorCount > 0 ? 'bg-red-100 text-red-700' : 'bg-slate-200 text-slate-600'}`}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ═══════════ ONGLET ACTIONS EN ATTENTE ═══════════ */}
      {mainTab === 'actions' && (
        <>
          {loadingActions ? (
            <div className="text-center py-16 text-slate-400">Chargement...</div>
          ) : (
            <div className="space-y-6">
              {/* Actions en attente */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="actions-grid">
                {[
                  { key: 'conges', label: 'Demandes de congé', count: actionBadges?.conges || 0, path: '/conges', color: 'bg-blue-50 border-blue-200 text-blue-700', iconCls: 'bg-blue-100 text-blue-600' },
                  { key: 'validation_heures', label: 'Heures à valider', count: actionBadges?.validation_heures || 0, path: '/validation-heures', color: 'bg-amber-50 border-amber-200 text-amber-700', iconCls: 'bg-amber-100 text-amber-600' },
                  { key: 'avertissements', label: 'Avertissements à approuver', count: actionBadges?.avertissements || 0, path: '/gestion-avertissements', color: 'bg-red-50 border-red-200 text-red-700', iconCls: 'bg-red-100 text-red-600' },
                  { key: 'todos', label: 'Tâches en retard', count: actionBadges?.todos || 0, path: '/todo-list', color: 'bg-purple-50 border-purple-200 text-purple-700', iconCls: 'bg-purple-100 text-purple-600' },
                  { key: 'incidents', label: 'Incidents ouverts', count: actionBadges?.incidents || 0, path: '/vehicules', color: 'bg-orange-50 border-orange-200 text-orange-700', iconCls: 'bg-orange-100 text-orange-600' },
                ].map(item => (
                  <a
                    key={item.key}
                    href={item.path}
                    data-testid={`action-card-${item.key}`}
                    className={`flex items-center gap-4 p-4 rounded-xl border transition-all hover:shadow-md ${item.count > 0 ? item.color : 'bg-slate-50 border-slate-200 text-slate-400'}`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold ${item.count > 0 ? item.iconCls : 'bg-slate-100 text-slate-400'}`}>
                      {item.count}
                    </div>
                    <div>
                      <p className={`font-semibold text-sm ${item.count > 0 ? '' : 'text-slate-400'}`}>{item.label}</p>
                      <p className="text-xs mt-0.5 opacity-70">{item.count > 0 ? 'Nécessite une action' : 'Aucune action requise'}</p>
                    </div>
                  </a>
                ))}
              </div>

              {/* Échecs non lus */}
              {failures.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-red-700 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Échecs d'envoi non lus ({failures.length})
                  </h3>
                  <div className="space-y-2">
                    {failures.map(f => (
                      <div key={f.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                        <div className="flex items-center gap-3 min-w-0">
                          <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-red-800 truncate">{f.event_id || f.template_code}</p>
                            <p className="text-xs text-red-600 truncate">{f.to} — {f.error?.slice(0, 80)}</p>
                            {f.sent_at && <p className="text-xs text-red-400">{new Date(f.sent_at).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' })}</p>}
                          </div>
                        </div>
                        <button onClick={() => handleDismiss(f.id)} className="ml-2 p-1 hover:bg-red-100 rounded shrink-0" title="Marquer comme lu">
                          <X className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    ))}
                    {failures.length > 0 && (
                      <Button variant="outline" size="sm" onClick={handleDismissAll} className="text-xs border-red-300 text-red-700 hover:bg-red-100">
                        <X className="w-3 h-3 mr-1" /> Tout marquer comme lu
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Derniers envois */}
              <div>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Derniers envois ({recentLogs.length} total)
                </h3>
                {recentLogs.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">Aucun envoi récent</p>
                ) : (
                  <div className="space-y-1.5">
                    {recentLogs.slice(0, recentLogsLimit).map((log, i) => {
                      const isEmail = log.channel === 'email';
                      const isError = log.status === 'erreur';
                      return (
                        <div key={log.id || i} className={`flex items-center gap-3 p-2.5 rounded-lg border text-sm ${isError ? 'bg-red-50 border-red-200' : 'bg-white border-slate-100'}`}>
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${isEmail ? 'bg-blue-100 text-blue-600' : 'bg-violet-100 text-violet-600'}`}>
                            {isEmail ? <Mail className="w-3.5 h-3.5" /> : <MessageCircle className="w-3.5 h-3.5" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-xs font-medium text-slate-700 truncate">{log.subject || log.template_code || log.event_id || '—'}</p>
                              {isError ? (
                                <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full font-medium shrink-0">Échec</span>
                              ) : (
                                <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium shrink-0">Envoyé</span>
                              )}
                            </div>
                            <p className="text-[11px] text-slate-400 truncate">{log.to || '—'}</p>
                          </div>
                          <span className="text-[10px] text-slate-400 shrink-0">
                            {log.sent_at ? new Date(log.sent_at).toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                      );
                    })}
                    {recentLogs.length > recentLogsLimit && (
                      <div className="text-center pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRecentLogsLimit(prev => prev + 20)}
                          className="text-xs"
                          data-testid="btn-voir-plus-logs"
                        >
                          Voir plus ({recentLogs.length - recentLogsLimit} restants)
                        </Button>
                      </div>
                    )}
                    {recentLogsLimit > 15 && (
                      <div className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRecentLogsLimit(15)}
                          className="text-xs text-slate-400"
                        >
                          Réduire
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {totalActions === 0 && failures.length === 0 && (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">Tout est en ordre !</p>
                  <p className="text-sm text-slate-400 mt-1">Aucune action en attente, aucun échec non lu.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ═══════════ ONGLET REGISTRE ═══════════ */}
      {mainTab === 'registre' && (
        <>
          {/* Sous-filtres */}
          <div className="space-y-2">
            {/* Filtres canaux/statut */}
            <div className="flex gap-1 bg-slate-50 border border-slate-200 p-1 rounded-lg flex-wrap">
              {[
                { id: 'all',        label: 'Tous',           count: events.length },
                { id: 'email',      label: 'Courriel',       count: events.filter(e => e.canal === 'email').length, icon: Mail },
                { id: 'sms',        label: 'SMS',            count: events.filter(e => e.canal === 'sms' || e.canal === 'sms+email').length, icon: MessageSquare },
                { id: 'errors',     label: 'Échecs',         count: errorCount, icon: XCircle, danger: errorCount > 0 },
                { id: 'implemente', label: 'À implémenter',  count: events.filter(e => e.implemente === false).length, icon: Clock },
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setRegistreFilter(tab.id)}
                    data-testid={`filter-registre-${tab.id}`}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-all ${
                      registreFilter === tab.id ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {Icon && <Icon className="w-3 h-3" />}
                    {tab.label}
                    {tab.count > 0 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${tab.danger ? 'bg-red-100 text-red-700' : 'bg-slate-200 text-slate-600'}`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Filtres par groupe */}
            <div className="flex gap-1 flex-wrap">
              {GROUPES.map(g => {
                const count = g === 'tous' ? events.length : events.filter(e => e.groupe === g).length;
                return (
                  <button
                    key={g}
                    onClick={() => setRegistreFilter(g === 'tous' ? 'all' : g)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                      (registreFilter === 'all' && g === 'tous') || registreFilter === g
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    {g === 'tous' ? 'Tous les groupes' : g} {count > 0 && `(${count})`}
                  </button>
                );
              })}
            </div>
          </div>

          {loadingEvents ? (
            <div className="text-center py-16 text-slate-400">Chargement du registre...</div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-16 text-slate-400">Aucun événement dans cette catégorie.</div>
          ) : (() => {
            // Grouper par groupe si filtre "all"
            const shouldGroup = registreFilter === 'all' || GROUPES.slice(1).includes(registreFilter);
            if (shouldGroup && registreFilter === 'all') {
              const groupes = [...new Set(filteredEvents.map(e => e.groupe))];
              return (
                <div className="space-y-6">
                  {groupes.map(g => {
                    const eventsInGroup = filteredEvents.filter(e => e.groupe === g);
                    return (
                      <div key={g}>
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">{g}</h3>
                          <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{eventsInGroup.length}</span>
                          <div className="flex-1 h-px bg-slate-100" />
                        </div>
                        <div className="space-y-2">
                          {eventsInGroup.map(event => (
                            <EventCard key={event.id} event={event} onTest={openTestDialog} onEdit={handleEditFromRegistre} onViewLogs={setLogsEventId} testing={testingId === event.id} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }
            return (
              <div className="space-y-2">
                {filteredEvents.map(event => (
                  <EventCard key={event.id} event={event} onTest={openTestDialog} onEdit={handleEditFromRegistre} onViewLogs={setLogsEventId} testing={testingId === event.id} />
                ))}
              </div>
            );
          })()}
        </>
      )}

      {/* ═══════════ ONGLET TEMPLATES ═══════════ */}
      {mainTab === 'templates' && (
        <>
          {/* Filtre catégorie */}
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-4 flex-wrap">
                <Label className="shrink-0">Catégorie :</Label>
                <Select value={filtreCategorie} onValueChange={setFiltreCategorie}>
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tous">Toutes les catégories</SelectItem>
                    {allCategories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {loadingTemplates ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
            </div>
          ) : templatesFiltres.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Aucun template pour le moment</p>
                <Button onClick={openCreateModal}><Plus className="w-4 h-4 mr-2" />Créer un template</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {templatesFiltres.map(template => {
                const catInfo = allCategories.find(c => c.value === template.categorie);
                const isSms = SMS_CATS.includes(template.categorie);
                const triggerText = template.code_sms ? SMS_TRIGGERS[template.code_sms] : null;
                const isSystemSms = !!template.code_sms;
                return (
                  <Card key={template.id} className={`hover:shadow-md transition-shadow ${isSms ? 'border-l-4 border-l-orange-400' : ''}`}>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <p className="font-bold text-base">{template.nom}</p>
                            <Badge className={catInfo?.color}>{catInfo?.label}</Badge>
                            {isSystemSms && (
                              <Badge className="bg-gray-100 text-gray-600 text-xs font-mono">{template.code_sms}</Badge>
                            )}
                          </div>
                          {triggerText && (
                            <div className="flex items-start gap-1.5 mb-3 bg-orange-50 border border-orange-100 rounded px-3 py-1.5">
                              <span className="text-xs text-orange-700"><strong>Déclencheur :</strong> {triggerText}</span>
                            </div>
                          )}
                          {!isSms && template.sujet && (
                            <p className="text-sm text-gray-600 mb-2"><strong>Sujet :</strong> {template.sujet}</p>
                          )}
                          <div className={`p-3 rounded mt-1 ${isSms ? 'bg-gray-900 text-green-400 font-mono' : 'bg-gray-50'}`}>
                            <p className={`text-sm whitespace-pre-wrap ${isSms ? 'text-green-400' : 'text-gray-700'}`}>
                              {isSms ? template.contenu : template.contenu.substring(0, 200) + (template.contenu.length > 200 ? '...' : '')}
                            </p>
                          </div>
                          {template.variables_disponibles?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500 mb-1">Variables disponibles :</p>
                              <div className="flex flex-wrap gap-1">
                                {template.variables_disponibles.map(v => (
                                  <Badge key={v} variant="outline" className="text-xs">{`{${v}}`}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4 shrink-0">
                          <Button variant="outline" size="sm" onClick={() => handleEditTemplate(template)} title="Modifier">
                            <Edit className="w-4 h-4" />
                          </Button>
                          {!isSystemSms && (
                            <Button variant="outline" size="sm" onClick={() => handleDeleteTemplate(template.id)} title="Supprimer">
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ═══════════ ONGLET HISTORIQUE ═══════════ */}
      {mainTab === 'historique' && (
        <>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="shrink-0 text-sm">Canal :</Label>
              <Select value={filtreCanal} onValueChange={setFiltreCanal}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tous">Tous (SMS + Courriel)</SelectItem>
                  <SelectItem value="sms">SMS seulement</SelectItem>
                  <SelectItem value="email">Courriel seulement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <span className="text-sm text-gray-500 ml-auto">{historique.length} enregistrement(s)</span>
          </div>

          {loadingHistorique ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Chargement de l'historique…</p>
            </div>
          ) : historique.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <History className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">Aucun message envoyé pour l'instant</p>
                <p className="text-gray-400 text-sm mt-1">Les SMS et courriels automatiques apparaîtront ici.</p>
              </CardContent>
            </Card>
          ) : (
            <>
            {/* Pagination en haut */}
            {historique.length > HIST_PER_PAGE && (
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-gray-500">Page {histPage} / {Math.ceil(historique.length / HIST_PER_PAGE)}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={histPage <= 1} onClick={() => setHistPage(p => p - 1)}>Précédent</Button>
                  <Button variant="outline" size="sm" disabled={histPage >= Math.ceil(historique.length / HIST_PER_PAGE)} onClick={() => setHistPage(p => p + 1)}>Suivant</Button>
                </div>
              </div>
            )}
            <Card>
              <CardContent className="p-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                        <th className="text-left px-4 py-3">Date / Heure</th>
                        <th className="text-left px-4 py-3">Canal</th>
                        <th className="text-left px-4 py-3">Template</th>
                        <th className="text-left px-4 py-3">Destinataire</th>
                        <th className="text-left px-4 py-3">Contenu</th>
                        <th className="text-left px-4 py-3">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {historique.slice((histPage - 1) * HIST_PER_PAGE, histPage * HIST_PER_PAGE).map((log, i) => {
                        const realIdx = (histPage - 1) * HIST_PER_PAGE + i;
                        const isEmail = log.channel === 'email';
                        const dateStr = log.sent_at
                          ? new Date(log.sent_at).toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '—';
                        const templateCode = log.template_code || log.context?.type || log.context?.template || '—';
                        const plainContent = (log.subject && isEmail ? `[${log.subject}] ` : '') + (log.body || '').replace(/<[^>]+>/g, '');
                        return (
                          <tr key={log.id || realIdx} className="hover:bg-gray-50 transition-colors" data-testid={`historique-row-${realIdx}`}>
                            <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-xs align-top">{dateStr}</td>
                            <td className="px-4 py-3 align-top">
                              {isEmail ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                                  <Mail className="w-3 h-3" /> Courriel
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs font-medium bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full">
                                  <MessageCircle className="w-3 h-3" /> SMS
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 align-top">
                              <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">{templateCode}</code>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600 max-w-[140px] align-top" title={log.to || log.to_raw || ''}>
                              <span className="break-all">{log.to || log.to_raw || '—'}</span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500 max-w-md align-top">
                              <span className="line-clamp-2">{plainContent?.substring(0, 120) || <span className="italic text-gray-300">—</span>}{plainContent?.length > 120 ? '…' : ''}</span>
                              {(log.body || log.subject) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setMessageDetailLog(log); }}
                                  data-testid={`view-message-${realIdx}`}
                                  className="mt-1 text-[11px] text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                >
                                  Voir le message complet
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-3 align-top">
                              {log.status === 'envoye' ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                                  <CheckCircle2 className="w-3 h-3" /> Envoyé
                                </span>
                              ) : log.status === 'template_absent' ? (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" title={log.error || ''}>
                                  <AlertCircle className="w-3 h-3" /> Template absent
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full" title={log.error || ''}>
                                  <XCircle className="w-3 h-3" /> Erreur
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            {/* Pagination en bas */}
            {historique.length > HIST_PER_PAGE && (
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-gray-500">Page {histPage} / {Math.ceil(historique.length / HIST_PER_PAGE)}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={histPage <= 1} onClick={() => { setHistPage(p => p - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Précédent</Button>
                  <Button variant="outline" size="sm" disabled={histPage >= Math.ceil(historique.length / HIST_PER_PAGE)} onClick={() => { setHistPage(p => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Suivant</Button>
                </div>
              </div>
            )}
            </>
          )}
        </>
      )}

      {/* ═══════════ DIALOG TEST ═══════════ */}
      <Dialog open={!!testDialog} onOpenChange={(open) => !open && setTestDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-blue-600" />
              Tester l'automatisation
            </DialogTitle>
          </DialogHeader>
          {testDialog && (
            <div className="space-y-4">
              {/* Info sur l'automatisation */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5 text-sm">
                <p><span className="text-slate-500">Automatisation :</span> <strong className="text-slate-800">{testDialog.label}</strong></p>
                <p><span className="text-slate-500">Déclencheur réel :</span> <span className="text-slate-700">{testDialog.trigger}</span></p>
                <p><span className="text-slate-500">Vrai destinataire :</span> <span className="text-slate-700 font-medium">{testDialog.destinataires?.join(', ')}</span></p>
                <p><span className="text-slate-500">Canal :</span>
                  <span className="ml-1 inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                    {testDialog.canal?.toUpperCase()}
                  </span>
                </p>
              </div>

              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Le test enverra un message <strong>avec la mention [TEST]</strong> vers les coordonnées que vous indiquez ci-dessous, et non vers le vrai destinataire.
              </div>

              {/* Champ email */}
              {(testDialog.canal === 'email' || testDialog.canal === 'sms+email') && (
                <div>
                  <Label className="text-sm">Adresse courriel de test <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={e => setTestEmail(e.target.value)}
                    placeholder="votre@email.com"
                    className="mt-1"
                  />
                </div>
              )}

              {/* Champ SMS */}
              {(testDialog.canal === 'sms' || testDialog.canal === 'sms+email') && (
                <div>
                  <Label className="text-sm">
                    Numéro de téléphone de test
                    {testDialog.canal === 'sms' && <span className="text-red-500"> *</span>}
                    {testDialog.canal === 'sms+email' && <span className="text-slate-400 font-normal"> (optionnel)</span>}
                  </Label>
                  <Input
                    type="tel"
                    value={testPhone}
                    onChange={e => setTestPhone(e.target.value)}
                    placeholder="Ex: +15819990854"
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-400 mt-1">Format international : +1 suivi du numéro à 10 chiffres</p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setTestDialog(null)}>Annuler</Button>
                <Button
                  onClick={handleTest}
                  disabled={testingId === testDialog.id}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                  data-testid="btn-confirm-test"
                >
                  <FlaskConical className="w-4 h-4" />
                  {testingId === testDialog.id ? 'Envoi...' : 'Envoyer le test'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════════ OVERLAY LOGS ═══════════ */}
      {logsEventId && (
        <LogsPanel eventId={logsEventId} headers={headers} onClose={() => setLogsEventId(null)} />
      )}

      {/* ═══════════ MODAL TEMPLATE ═══════════ */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Modifier' : 'Créer'} un Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom du Template <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="Ex: Confirmation Rendez-vous"
                  value={formData.nom}
                  onChange={e => setFormData(p => ({ ...p, nom: e.target.value }))}
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <div className="flex gap-2">
                  <Select value={formData.categorie} onValueChange={val => setFormData(p => ({ ...p, categorie: val }))}>
                    <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {allCategories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowAddCategorie(v => !v)} className="shrink-0">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {showAddCategorie && (
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newCategorieName}
                      onChange={e => setNewCategorieName(e.target.value)}
                      placeholder="Nom de la catégorie..."
                      className="flex-1 h-8 text-sm"
                      onKeyDown={e => e.key === 'Enter' && handleAddCategorie()}
                    />
                    <Button type="button" size="sm" onClick={handleAddCategorie} className="h-8">Ajouter</Button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Sujet de l'Email {!isSmsCategory && <span className="text-red-500">*</span>}</Label>
              {isSmsCategory ? (
                <p className="text-xs text-gray-400 italic mt-1">Pas de sujet pour les SMS — champ non applicable.</p>
              ) : (
                <Input
                  placeholder="Ex: Confirmation de votre rendez-vous"
                  value={formData.sujet}
                  onChange={e => setFormData(p => ({ ...p, sujet: e.target.value }))}
                />
              )}
            </div>
            <div>
              <Label>Contenu du Message <span className="text-red-500">*</span></Label>
              <Textarea
                placeholder="Bonjour {client_nom},&#10;&#10;Votre rendez-vous est confirmé pour le {date_rdv}..."
                value={formData.contenu}
                onChange={e => setFormData(p => ({ ...p, contenu: e.target.value }))}
                rows={10}
              />
            </div>
            <div className="bg-blue-50 p-4 rounded">
              <div className="flex items-start gap-2 mb-2">
                <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900">Variables disponibles</p>
                  <p className="text-xs text-blue-700">Cliquez pour insérer dans le message</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.variables_disponibles.map(variable => (
                  <Button key={variable} type="button" size="sm" variant="outline" className="text-xs" onClick={() => insererVariable(variable)}>
                    {`{${variable}}`}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
              <Button onClick={handleSaveTemplate}>{editingTemplate ? 'Modifier' : 'Créer'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══════════ MODAL MESSAGE COMPLET ═══════════ */}
      {messageDetailLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="message-detail-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="min-w-0">
                <h3 className="font-semibold text-slate-800 text-sm">Message envoyé</h3>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                  {messageDetailLog.channel === 'email' ? (
                    <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      <Mail className="w-3 h-3" /> Courriel
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                      <MessageCircle className="w-3 h-3" /> SMS
                    </span>
                  )}
                  <span>Destinataire : <strong>{messageDetailLog.to || messageDetailLog.to_raw || '—'}</strong></span>
                  {messageDetailLog.sent_at && (
                    <span>{new Date(messageDetailLog.sent_at).toLocaleString('fr-CA', { dateStyle: 'long', timeStyle: 'short' })}</span>
                  )}
                </div>
              </div>
              <button onClick={() => setMessageDetailLog(null)} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
            </div>
            {messageDetailLog.subject && (
              <div className="px-4 py-2 bg-slate-50 border-b text-sm">
                <span className="text-slate-500">Sujet :</span>{' '}
                <span className="font-medium text-slate-800">{messageDetailLog.subject}</span>
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-4">
              {messageDetailLog.channel === 'email' && messageDetailLog.body?.includes('<') ? (
                <iframe
                  title="Message complet"
                  srcDoc={messageDetailLog.body}
                  className="w-full border border-slate-200 rounded-lg"
                  style={{ minHeight: '400px', height: '100%' }}
                  sandbox=""
                  data-testid="message-detail-iframe"
                />
              ) : (
                <div className="whitespace-pre-wrap break-words text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="message-detail-text">
                  {messageDetailLog.body || <span className="italic text-slate-400">Aucun contenu</span>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
